import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  VerifyBulkCommisionUpload,
  BulkUploadCommision as PostBulkUploadCommision,
} from "../../../../Service/SearchPolicy";
import { ToastError, ToastSuccess } from "../../../../UiComponents/Toaster/Toast";

const BulkUploadCommision = ({ open, setOpen, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [verificationResults, setVerificationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const savingRef = useRef(false); // a second click before the screen re-renders must not add the same amounts twice

  const handleClose = () => {
    setOpen(false);
    setSelectedFile(null);
    setVerificationResults([]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setVerificationResults([]);
    }
  };

  const downloadSampleExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sample");

    worksheet.columns = [
      { header: "Policy_Number", key: "Policy_Number", width: 25 },
      { header: "Insertamount", key: "Insertamount", width: 15 },
      { header: "BankName", key: "BankName", width: 20 },
      { header: "Remark", key: "Remark", width: 30 },
    ];

    const headerRow = worksheet.getRow(1);

    // Apply yellow background to headers
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF00" }, // Yellow
      };
      cell.font = {
        bold: true,
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "Sample_Receivable_Upload.xlsx");
  };

  const handleVerify = async () => {
    if (!selectedFile) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await VerifyBulkCommisionUpload(formData);
      if (response && !response.error) {
        // Check for specific header validation error from backend
        if (response.data && response.data.length > 0 && response.data[0].error) {
          ToastError(response.data[0].message || "Invalid Excel headers");
          setVerificationResults([]);
        } else {
          setVerificationResults(response.data);
          ToastSuccess("File verified successfully");
        }
      } else {
        ToastError(response.message || "Verification failed");
      }
    } catch (error) {
      console.error("Verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) return;

    // Block save for any row with a problem: not found, cancelled, duplicate, an amount that is not a number, or more than is
    // pending (the server refuses those rows as well).
    const hasErrors = verificationResults.some(res =>
      res.error ||
      res.status === "Duplicate in File" ||
      res.status === "Not Found"
    );

    if (hasErrors) {
      ToastError("Please resolve all errors (highlighted in red) before saving.");
      return;
    }

    if (savingRef.current) return;
    savingRef.current = true;
    setSaveLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await PostBulkUploadCommision(formData);
      if (response && !response.error) {
        // header-invalid path answers { success: false, message }
        if (response.data && response.data.success === false) {
          ToastError(response.data.message || "Upload failed");
          return;
        }
        ToastSuccess(`${response.data?.success || 0} policies updated successfully!`);
        const failed = response.data?.failed || 0;
        if (failed > 0) {
          ToastError(`${failed} rows were skipped (the policy changed since the check, or the amount is no longer allowed).`);
        }
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        ToastError(response?.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      ToastError("Upload failed");
    } finally {
      savingRef.current = false;
      setSaveLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Bulk Upload Commision</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        {verificationResults.length === 0 ? (
          <>
            <Box
              sx={{
                border: "2px dashed #E4E7EC",
                borderRadius: "8px",
                p: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                backgroundColor: "#F9FAFB",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#F2F4F7",
                },
              }}
              component="label"
            >
              <input
                type="file"
                hidden
                accept=".xlsx, .xls"
                onChange={handleFileChange}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: "#667085" }} />
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedFile ? selectedFile.name : "Click to upload Excel file"}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Only .xlsx and .xls files are supported
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={downloadSampleExcel}
                sx={{
                  textTransform: "none",
                  color: "#667085",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#1D2939",
                  },
                }}
              >
                Download Sample Excel
              </Button>
            </Box>
          </>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500, borderRadius: "8px", overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Policy Number",
                    "Total Amount",
                    "Enter Amount",
                    "Pending Amount",
                    "Still Pending",
                    "Bank Name",
                    "Remark",
                    "Status",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#f8f9fa",
                        color: "#475467",
                        borderBottom: "2px solid #eaecf0",
                        py: 1.5,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {verificationResults.map((result, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: result.status === "Not Found" || result.error ? "#FFF5F5" : "inherit",
                      "&:hover": { backgroundColor: "#f9fafb" },
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>{result.policyNumber}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{result.totalAmount ? Number(result.totalAmount).toFixed(2) : "-"}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{result.enterAmount ? Number(result.enterAmount).toFixed(2) : 0}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{result.pendingAmount ? Number(result.pendingAmount).toFixed(2) : 0}</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600 }}>
                      {result.stillPending !== undefined ? Number(result.stillPending).toFixed(2) : 0}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>{result.bankName || "-"}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>{result.remark || "-"}</TableCell>
                    <TableCell
                      sx={{
                        py: 1.5,
                        color: result.status === "Not Found" || result.error ? "error.main" : "success.main",
                        fontWeight: 600,
                      }}
                    >
                      {result.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, px: 3 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}>
          {verificationResults.length > 0 ? "Cancel" : "Close"}
        </Button>
        {verificationResults.length === 0 ? (
          <Button
            variant="contained"
            disabled={!selectedFile || loading}
            onClick={handleVerify}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              backgroundColor: "#E50000",
              "&:hover": {
                backgroundColor: "#B30000",
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Verify"}
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={saveLoading}
            onClick={handleSave}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              backgroundColor: "#10B981",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}
          >
            {saveLoading ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadCommision;
